import {
  addCallerNumberResponseSchema,
  callerNumberResponseSchema,
  callerNumbersResponseSchema,
  type AddCallerNumberBody,
  type AssignCallerNumberBody,
  type CallerNumberRow,
  type CallerNumbersResponse,
} from "@gioia365/contracts";
import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiRequestError, apiRequest } from "../api/http";
import { tabKey, type TabContext } from "../session/tab-context";

// Il parco dei cellulari dell'ufficio (piano M4, Task 17; rotte del Task 16, `/api/caller-numbers`): l'elenco, che leggono chi
// gestisce il team e chi ha la sezione «Numeri per chiamare», e le scritture. Ogni richiesta porta l'azienda della scheda e
// la chiave sta sotto la sua (`["scheda", tabKey(tab), "caller-numbers"]`), tolta con le altre al cambio di azienda. Ogni
// scrittura, riuscita o rifiutata, rilegge l'elenco: il parco mostra ciò che il server ha davvero (anche dopo un
// `409 version_conflict`).
//
// Il codice di sei cifre della verifica arriva nella risposta di `POST` e di `POST …/:id/verify` e vale soltanto per chi tiene
// il telefono: quelle due scritture non lo tengono nella cache (`gcTime: 0`), e chi le usa lo copia nel suo stato e chiama
// subito `reset()` (`add-caller-number-dialog.tsx`), come il numero mostrato della telefonia (R2).

const BASE = "/api/caller-numbers";
const numberPath = (id: string) => `${BASE}/${encodeURIComponent(id)}`;

export function callerNumbersKey(tab: TabContext) {
  return ["scheda", tabKey(tab), "caller-numbers"] as const;
}

/**
 * `GET /api/caller-numbers`: i cellulari non tolti, in ordine di ingresso, con ciò che chi guarda può fare. `null` quando il
 * server non lo lascia leggere (`403`), e quando non ha le rotte del parco: un server senza la telefonia (la demo locale) non
 * le monta e risponde il `404 not_found` di ogni rotta che non c'è, come per la chiamata aperta (`openCallQuery`). Allora la
 * colonna e la sezione semplicemente non ci sono, senza errore e senza nuovi tentativi. Ogni altro rifiuto o guasto resta un
 * errore, che la sezione dice con «Riprova».
 */
export function callerNumbersQuery(tab: TabContext) {
  return queryOptions({
    queryKey: callerNumbersKey(tab),
    queryFn: async ({ signal }): Promise<CallerNumbersResponse | null> => {
      try {
        return await apiRequest("GET", BASE, { schema: callerNumbersResponseSchema, tab, signal });
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 403 && error.code === "permission_denied") return null;
        if (error instanceof ApiRequestError && error.status === 404 && error.code === "not_found") return null;
        throw error;
      }
    },
  });
}

/** Il cellulare dato a una persona, se ne ha uno (ognuno ne ha al più uno, anche se è pure di altre persone: TEL-3). */
export function numberOf(pool: CallerNumbersResponse, membershipId: string): CallerNumberRow | null {
  return pool.numbers.find((row) => row.assignees.some((person) => person.membershipId === membershipId)) ?? null;
}

function useRefreshPool(tab: TabContext) {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: callerNumbersKey(tab) });
}

/** «Aggiungi un cellulare» (soltanto la piattaforma): il cellulare entra nel parco e il fornitore chiama per la verifica. */
export function useAddCallerNumber(tab: TabContext) {
  const refresh = useRefreshPool(tab);
  return useMutation({
    mutationFn: (body: AddCallerNumberBody) => apiRequest("POST", BASE, { schema: addCallerNumberResponseSchema, body, tab }),
    gcTime: 0,
    onSettled: refresh,
  });
}

/** «Verifica» di un cellulare in verifica o non riuscito (soltanto la piattaforma): un codice nuovo. */
export function useVerifyCallerNumber(tab: TabContext) {
  const refresh = useRefreshPool(tab);
  return useMutation({
    mutationFn: (id: string) => apiRequest("POST", `${numberPath(id)}/verify`, { schema: addCallerNumberResponseSchema, tab }),
    gcTime: 0,
    onSettled: refresh,
  });
}

/**
 * «Leggi i cellulari già verificati» (soltanto la piattaforma): la risposta è l'elenco intero, che prende subito il posto di
 * quello in cache; poi, come dopo ogni altra scrittura, l'elenco si rilegge anche quando l'importazione riesce (sullo staging,
 * il 5 ottobre 2026, il parco restava vuoto finché non si ricaricava la pagina).
 */
export function useImportCallerNumbers(tab: TabContext) {
  const queryClient = useQueryClient();
  const refresh = useRefreshPool(tab);
  return useMutation({
    mutationFn: () => apiRequest("POST", `${BASE}/import`, { schema: callerNumbersResponseSchema, tab }),
    onSuccess: async (pool) => {
      await queryClient.cancelQueries({ queryKey: callerNumbersKey(tab) });
      queryClient.setQueryData(callerNumbersKey(tab), pool);
    },
    onSettled: refresh,
  });
}

/** Dà il cellulare a una persona attiva, o glielo toglie, con la versione letta: le altre persone del cellulare non cambiano. */
export function useAssignCallerNumber(tab: TabContext) {
  const refresh = useRefreshPool(tab);
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AssignCallerNumberBody }) =>
      apiRequest("PUT", `${numberPath(id)}/assignee`, { schema: callerNumberResponseSchema, body, tab }),
    onSettled: refresh,
  });
}

/** «Togli dal parco» (soltanto la piattaforma): chi lo aveva lo perde, tutte le persone; le chiamate passate lo tengono. */
export function useRemoveCallerNumber(tab: TabContext) {
  const refresh = useRefreshPool(tab);
  return useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", numberPath(id), { schema: callerNumberResponseSchema, tab }),
    onSettled: refresh,
  });
}
